import LeaveCalendar from "pages/dashboard/people/requests/all/leaves/LeaveCalendar"
import LeavePlanningRoster from "pages/dashboard/people/requests/all/leaves/LeavePlanningRoster"
import PendingLeaveReport from "pages/dashboard/people/requests/all/leaves/PendingLeaveReport"
import LeaveAnalytics from "pages/dashboard/people/requests/all/leaves/LeaveAnalytics"
import RequireRoutePermit from "RequireRoutePermit"
import { RouteFallback } from "components/new-stuff/layout/RouteFallback"
import React from "react"
import { Navigate, Route } from "react-router-dom"

const ChooseRequest = React.lazy(
  () => import("../pages/dashboard/people/requests/ChooseRequest")
)
const Requisitions = React.lazy(
  () => import("pages/dashboard/people/requests/requisitions/Requisitions")
)
const RequisitionSetup = React.lazy(
  () => import("pages/dashboard/people/requests/requisitions/RequisitionSetup")
)

const LeaveRequests = React.lazy(
  () => import("../pages/dashboard/people/requests/all/leaves/LeaveRequests")
)

const CreateLeaveRequest = React.lazy(
  () =>
    import("../pages/dashboard/people/requests/all/leaves/CreateLeaveRequest")
)

const ViewLeavePolicy = React.lazy(
  () =>
    import(
      "../pages/dashboard/people/requests/policies/leave-policy/ViewLeavePolicy"
    )
)
const ViewDocumentPolicy = React.lazy(
  () =>
    import(
      "pages/dashboard/people/requests/policies/document-policies/ViewDocumentPolicy"
    )
)

const ViewExitPolicy = React.lazy(
  () =>
    import(
      "pages/dashboard/people/requests/policies/exit-policies/ViewExitPolicy"
    )
)

const ViewLoanPolicy = React.lazy(
  () =>
    import(
      "pages/dashboard/people/requests/policies/loan-salaries-policies/ViewLoanPolicy"
    )
)

const ViewRedeploymentPolicy = React.lazy(
  () =>
    import(
      "pages/dashboard/people/requests/policies/redeployment-policies/ViewRedeploymentPolicy"
    )
)

const ViewAdvancePolicy = React.lazy(
  () =>
    import(
      "pages/dashboard/people/requests/policies/loan-salaries-policies/ViewAdvancePolicy"
    )
)

const LoanRequests = React.lazy(
  () => import("../pages/dashboard/people/requests/all/loans/LoanRequests")
)

const SalaryAdvanceRequests = React.lazy(
  () =>
    import(
      "../pages/dashboard/people/requests/all/salary-advances/SalaryAdvanceRequests"
    )
)

const DocumentRequests = React.lazy(
  () =>
    import("../pages/dashboard/people/requests/all/documents/DocumentRequests")
)
const ExitRequests = React.lazy(
  () => import("../pages/dashboard/people/requests/all/exits/ExitRequests")
)
const RedeploymentRequests = React.lazy(
  () =>
    import(
      "../pages/dashboard/people/requests/all/redeployments/RedeploymentRequests"
    )
)

const CreateLoanRequest = React.lazy(
  () => import("../pages/dashboard/people/requests/all/loans/CreateLoanRequest")
)
const CreateEmployeeDocumentRequest = React.lazy(
  () =>
    import(
      "../pages/dashboard/people/requests/all/documents/CreateEmployeeDocumentRequest"
    )
)
const CreateSalaryAdvanceRequest = React.lazy(
  () =>
    import(
      "../pages/dashboard/people/requests/all/salary-advances/CreateSalaryAdvanceRequest"
    )
)
const CreateExitRequest = React.lazy(
  () => import("../pages/dashboard/people/requests/all/exits/CreateExitRequest")
)
const BulkExitRequests = React.lazy(
  () => import("../pages/dashboard/people/requests/all/exits/BulkExitsUpload")
)
const CreateRedeploymentRequest = React.lazy(
  () =>
    import(
      "../pages/dashboard/people/requests/all/redeployments/CreateRedeploymentRequest"
    )
)
const BulkRedeployments = React.lazy(
  () =>
    import(
      "../pages/dashboard/people/requests/all/redeployments/BulkUploadRedeployment"
    )
)

const EmployeeChangesRequests = React.lazy(
  () =>
    import(
      "../pages/dashboard/people/requests/all/employee-changes/EmployeeChangesRequests"
    )
)

const Requests = () => {
  return (
    <>
      <Route path="list">
        <Route
          path=""
          element={<Navigate to="choose" />}
        />
        <Route
          path="choose"
          element={<ChooseRequest />}
        />
        <Route
          path="requisitions"
          element={
            <RequireRoutePermit
              allowedPermissions={["can_fulfill_requisitions"]}
            >
              <Requisitions />
            </RequireRoutePermit>
          }
        />
        <Route
          path="leaves"
          element={<LeaveRequests />}
        />
        <Route
          path="documents"
          element={<DocumentRequests />}
        />
        <Route
          path="exits"
          element={<ExitRequests />}
        />
        <Route
          path="redeployments"
          element={<RedeploymentRequests />}
        />
        <Route
          path="loans"
          element={<LoanRequests />}
        />
        <Route
          path="salary-advances"
          element={<SalaryAdvanceRequests />}
        />
        <Route
          path="employee-changes"
          element={<EmployeeChangesRequests />}
        />
      </Route>
      <Route
        path="requisitions/setup"
        element={
          <RequireRoutePermit allowedPermissions={["can_fulfill_requisitions"]}>
            <RequisitionSetup />
          </RequireRoutePermit>
        }
      />
      <Route path="leave">
        {/* LC. No permission guard: every employee may see their own leave, and the server
            narrows the scope to what they are actually allowed to look at. */}
        <Route
          path="calendar"
          element={<LeaveCalendar audience="admin" />}
        />
        {/* AL-05/06. No permission guard: the service narrows the roster to the caller's own
            department unless they may look across leave, so the screen is safe for anyone. */}
        <Route
          path="planning-roster"
          element={<LeavePlanningRoster />}
        />
        {/* An admin's own plan is made on the employee side, like anyone else's. */}
        <Route
          path="my-plan"
          element={
            <Navigate
              to="/employee/dashboard/requests/leave?view=plan"
              replace
            />
          }
        />
        {/* RP-03. Chasing support, standing in for the escalation V2 does not have. */}
        <Route
          path="pending-report"
          element={
            <RequireRoutePermit
              allowedPermissions={["can_view_leave_requests"]}
            >
              <PendingLeaveReport />
            </RequireRoutePermit>
          }
        />
        {/* RP. Also reachable as the Reports tab on the leave screen; kept as a route of its
            own so a saved link to a report still opens. */}
        <Route
          path="reports"
          element={
            <RequireRoutePermit
              allowedPermissions={["can_view_leave_requests"]}
            >
              <LeaveAnalytics />
            </RequireRoutePermit>
          }
        />
        <Route
          path="create"
          element={
            <RequireRoutePermit
              allowedPermissions={["can_create_leave_requests"]}
            >
              <CreateLeaveRequest />
            </RequireRoutePermit>
          }
        />

        <Route
          path="policy/:key"
          element={
            <RequireRoutePermit
              allowedPermissions={["can_view_leave_policies"]}
            >
              <ViewLeavePolicy />
            </RequireRoutePermit>
          }
        />
      </Route>
      <Route path="loan">
        <Route
          path="create"
          element={
            <RequireRoutePermit
              allowedPermissions={["can_create_loan_requests"]}
            >
              <CreateLoanRequest />
            </RequireRoutePermit>
          }
        />
        <Route
          path="policy/:key"
          element={
            <RequireRoutePermit allowedPermissions={["can_view_loan_policies"]}>
              <ViewLoanPolicy />
            </RequireRoutePermit>
          }
        />
      </Route>
      <Route path="salary-advance">
        <Route
          path="create"
          element={
            <RequireRoutePermit
              allowedPermissions={["can_view_salary_advance_requests"]}
            >
              <CreateSalaryAdvanceRequest />
            </RequireRoutePermit>
          }
        />
        <Route
          path="policy/:key"
          element={
            <RequireRoutePermit
              allowedPermissions={["can_view_salary_advance_policies"]}
            >
              <ViewAdvancePolicy />
            </RequireRoutePermit>
          }
        />
      </Route>
      <Route path="document">
        <Route
          path="create"
          element={
            <RequireRoutePermit
              allowedPermissions={["can_create_document_requests"]}
            >
              <CreateEmployeeDocumentRequest />
            </RequireRoutePermit>
          }
        />
        <Route
          path="policy/:key"
          element={
            <RequireRoutePermit
              allowedPermissions={["can_view_document_policies"]}
            >
              <ViewDocumentPolicy />
            </RequireRoutePermit>
          }
        />
      </Route>
      <Route path="exit">
        <Route
          path="create"
          element={
            <RequireRoutePermit
              allowedPermissions={["can_create_exit_requests"]}
            >
              <CreateExitRequest />
            </RequireRoutePermit>
          }
        />
        <Route
          path="bulk-upload"
          element={
            <RequireRoutePermit
              allowedPermissions={["can_create_exit_requests"]}
            >
              <BulkExitRequests />
            </RequireRoutePermit>
          }
        />
        <Route
          path="policy/:key"
          element={
            <RequireRoutePermit allowedPermissions={["can_view_exit_policies"]}>
              <ViewExitPolicy />
            </RequireRoutePermit>
          }
        />
      </Route>
      <Route path="redeployment">
        <Route
          path="create"
          element={
            <RequireRoutePermit
              allowedPermissions={["can_create_redeployment_requests"]}
            >
              <CreateRedeploymentRequest />
            </RequireRoutePermit>
          }
        />
        <Route
          path="bulk-upload"
          element={
            <RequireRoutePermit
              allowedPermissions={["can_create_redeployment_requests"]}
            >
              <BulkRedeployments />
            </RequireRoutePermit>
          }
        />
        <Route
          path="policy/:key"
          element={
            <RequireRoutePermit
              allowedPermissions={["can_view_redeployment_policies"]}
            >
              <ViewRedeploymentPolicy />
            </RequireRoutePermit>
          }
        />
      </Route>
    </>
  )
}

export default Requests
